import { Modal, Row, Col, Input, DatePicker } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { t_billing_process } from 'types';
import { updateInvoice } from '../thunk';
import Wrap from './styles';

const { TextArea } = Input;

type Props = {
  data: t_billing_process | null;
  visible: boolean;
  handleChangeVisible: (value: boolean) => void;
  afterUpdate: () => void;
};

const formatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
});

const ModalRegister: FC<Props> = ({ data, visible, handleChangeVisible, afterUpdate }) => {
  const dispatch = useDispatch();
  const [paymentAmount, setPaymentAmount] = useState(
    isNaN(Number(data?.payment_amount)) ? undefined : Number(data?.payment_amount),
  );
  const [paymentDate, setPaymentDate] = useState(data?.payment_date);
  const [paymentRemark, setPaymentRemark] = useState(data?.payment_remark);
  const [paymentFees, setPaymentFees] = useState(data?.payment_fees);
  const [carriedFoward, setCarriedFoward] = useState(data?.carried_forward);

  const disable = data?.Status === 'Closed' || data?.Status === '終了';

  useEffect(() => {
    if (data) {
      if (data?.payment_method === '振込') {
        setPaymentFees(isNaN(Number(data?.payment_fees ?? 0)) ? 0 : Number(data?.payment_fees ?? 0));
        setCarriedFoward(
          Number(data?.billing_amount ?? 0) - Number(data?.payment_amount ?? 0) - Number(data?.payment_fees ?? 0),
        );
      } else {
        setCarriedFoward(Number(data?.billing_amount ?? 0) - Number(data?.payment_amount ?? 0));
        setPaymentFees(isNaN(Number(data?.payment_fees ?? 0)) ? 0 : Number(data?.payment_fees ?? 0));
      }
      setPaymentAmount(isNaN(Number(data?.payment_amount)) ? 0 : Number(data?.payment_amount));
      setPaymentDate(data?.payment_date);
      setPaymentRemark(data?.payment_remark);
    }
  }, [data]);

  const changePaymentAmount = (value: string) => {
    const pay = Number(value);
    if (!isNaN(Number(pay))) {
      setPaymentAmount(pay);
      if (data?.payment_method === '振込') {
        if (pay > Number(data?.billing_amount)) {
          setPaymentFees(0);
          setCarriedFoward(Number(data?.billing_amount) - pay);
        } else {
          setPaymentFees(Number(data?.billing_amount) - pay);
          setCarriedFoward(0);
        }
      } else {
        setCarriedFoward(Number(data?.billing_amount) - pay);
      }
    }
  };

  const changePaymentFees = (value: number) => {
    setPaymentFees(Number(value));
    if (data?.payment_method !== '振込') {
      return;
    }
    const newCarried = Number(data?.billing_amount ?? 0) - Number(paymentAmount) - Number(value);
    setCarriedFoward(newCarried);
  };

  const cancelModal = () => {
    setPaymentAmount(isNaN(Number(data?.payment_amount)) ? 0 : Number(data?.payment_amount));
    setPaymentDate(data?.payment_date);
    setPaymentRemark(data?.payment_remark);
    setPaymentFees(isNaN(Number(data?.payment_fees)) ? 0 : Number(data?.payment_fees));
    setCarriedFoward(isNaN(Number(data?.carried_forward)) ? 0 : Number(data?.carried_forward));
    handleChangeVisible(false);
  };

  const updateData = async () => {
    if (data?.i_id) {
      await dispatch(
        updateInvoice({
          i_id: data?.i_id,
          Status: data?.Status,
          payment_method: data.payment_method,
          payment_amount: paymentAmount,
          payment_date: paymentDate,
          payment_remark: paymentRemark,
          payment_fees: paymentFees,
          carried_forward: carriedFoward,
        }),
      );
      await afterUpdate();
      setPaymentAmount(undefined);
      setPaymentDate('');
      setPaymentRemark('');
      setPaymentFees(undefined);
      setCarriedFoward(undefined);
      handleChangeVisible(false);
    }
  };

  return (
    <div>
      <Modal
        title={
          data
            ? `${data.invoice_month?.slice(0, 4)}年${Number(data.invoice_month?.slice(4))}月度  ${
                data.payment_method
              }入金処理`
            : ''
        }
        centered
        visible={visible}
        closable={false}
        onOk={updateData}
        onCancel={cancelModal}
        okText="入金登録"
        cancelText="閉じる"
        width={1208}
        okButtonProps={{ style: { display: disable ? 'none' : 'block' } }}
        bodyStyle={{ backgroundColor: '#eeeeee', paddingBottom: 30 }}
      >
        <Wrap>
          <div className="info-clinic">
            <div className="name-clinic extraLargeFont">{data?.facility_id}</div>
            <Row className="clinic">
              <Col className="col-clinic" span={7}>
                <Row className="info">
                  <Col className="smallBoldFont" span={10}>
                    取引先番号
                  </Col>
                  <Col className="standardFont" span={12}>
                    {data?.facility_code}
                  </Col>
                </Row>
                <Row className="info">
                  <Col className="smallBoldFont" span={10}>
                    CPAP
                  </Col>
                  <Col className="standardFont" span={12}>
                    {formatter
                      .format(
                        Number(data?.cpap_unit_price) * Number(data?.cpap_count) +
                          Number(data?.cpap_unit_sp_price) * Number(data?.cpap_sp_count),
                      )
                      .slice(-3) === 'NaN'
                      ? '-'
                      : formatter.format(
                          Number(data?.cpap_unit_price) * Number(data?.cpap_count) +
                            Number(data?.cpap_unit_sp_price) * Number(data?.cpap_sp_count),
                        )}
                  </Col>
                </Row>
                <Row className="info">
                  <Col className="smallBoldFont" span={10}>
                    PSG
                  </Col>
                  <Col className="standardFont" span={12}>
                    {formatter.format(Number(data?.psg_unit_price) * Number(data?.psg_count)).slice(-3) === 'NaN'
                      ? '-'
                      : formatter.format(Number(data?.psg_unit_price) * Number(data?.psg_count))}
                  </Col>
                </Row>
                <Row className="info">
                  <Col className="smallBoldFont" span={10}>
                    簡易PSG
                  </Col>
                  <Col className="standardFont" span={12}>
                    {formatter
                      .format(Number(data?.simplicity_unit_price) * Number(data?.simplicity_count))
                      .slice(-3) === 'NaN'
                      ? '-'
                      : formatter.format(Number(data?.simplicity_unit_price) * Number(data?.simplicity_count))}
                  </Col>
                </Row>
              </Col>
              <Col className="col-bill" span={12}>
                <Row className="bill bill-month">
                  <Col className="bill-title extraLargeFont" span={7}>
                    {Number(data?.invoice_month?.slice(4))}月度請求額
                  </Col>
                  <Col className="bill-month-amount extraLargeFont" span={16}>
                    {formatter.format(Number(data?.billing_amount)).slice(-3) === 'NaN'
                      ? formatter.format(0)
                      : formatter.format(Number(data?.billing_amount))}
                  </Col>
                </Row>
                <Row className="bill bill-forward">
                  <Col className="bill-title extraLargeFont color-diff" span={7}>
                    前回繰越金額
                  </Col>
                  <Col className="bill-month-amount extraLargeFont" span={16}>
                    {formatter.format(Number(data?.carryover_amount)).slice(-3) === 'NaN'
                      ? formatter.format(0)
                      : formatter.format(Number(data?.carryover_amount))}
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <div className="deposit-amount">
            <Row className="middleBoldFont">入金額を入力してください</Row>
            <Row>
              <Col span={9}>
                <Row className="standardBoldFont input-deposit">
                  <Col style={{ marginTop: '9px' }} span={5}>
                    入金額
                  </Col>
                  <Col className="input-deposit" span={17}>
                    <label className="mr-10" htmlFor="deposit_amount">
                      ￥
                    </label>
                    <Input
                      size="large"
                      disabled={disable}
                      value={paymentAmount}
                      onChange={(e) => changePaymentAmount(e.target.value)}
                      id="deposit_amount"
                    />
                  </Col>
                </Row>
                <Row className="standardBoldFont input-deposit">
                  <Col style={{ marginTop: '9px' }} className="mr-24" span={5}>
                    入金日
                  </Col>
                  <Col className="input-deposit" span={17}>
                    <DatePicker
                      disabled={disable}
                      size="large"
                      name="date"
                      format="YYYY/MM/DD"
                      value={paymentDate ? moment(paymentDate) : undefined}
                      onChange={(e) => setPaymentDate(e?.toString())}
                      style={{ width: '254.4px' }}
                      placeholder=""
                    />
                  </Col>
                </Row>
                {data?.payment_method === '振込' && (
                  <Row className="standardBoldFont input-deposit">
                    <Col style={{ marginTop: '9px' }} className="mr-24" span={5}>
                      振込手数料
                    </Col>
                    <Col className="input-deposit" span={17}>
                      <Input
                        disabled={disable}
                        size="large"
                        type="number"
                        value={paymentFees}
                        onChange={(e) => changePaymentFees(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder=""
                      />
                    </Col>
                  </Row>
                )}
              </Col>
              {data && data.Status !== '請求書登録済' && data?.Status !== '請求書発行済' && (
                <Col style={{ marginTop: '15px' }} span={12}>
                  <Row className="bill-add bill-month">
                    <Col className="bill-title extraLargeFont" span={7}>
                      次回繰越金額
                    </Col>
                    <Col className="bill-add-amount extraLargeFont" span={16}>
                      {formatter.format(carriedFoward ?? 0)}
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
            <Row style={{ marginTop: 15 }} className="standardBoldFont">
              <Col span={2} style={{ marginRight: 18 }}>
                備考
              </Col>
              <Col span={18}>
                <TextArea
                  disabled={disable}
                  value={paymentRemark}
                  onChange={(e) => setPaymentRemark(e.target.value)}
                  style={{ width: 751, height: 126 }}
                />
              </Col>
            </Row>
          </div>
        </Wrap>
      </Modal>
    </div>
  );
};
export default ModalRegister;
